import { Archive, CircleCheck, Folder, List, Pencil, Plus, RefreshCw, Save, X } from 'lucide-react'

import { QueryState } from '@krp/sale-ui'
import { Button, CheckboxField, SelectField, TextField } from '@krp/ui-kit'

import type { OwnGroupsActions, OwnGroupsVm } from '../model/types'
import { CommandError, Problem } from './parts'
import './own-nomenclature.css'
import { ICON_SIZE, ICON_SIZE_SMALL } from '../../../ownIcons'

const indent = (depth: number) => (depth <= 0 ? undefined : `own-indent-${Math.min(depth, 3)}`)

/**
 * Группы товаров своей базы деревом: создать, переименовать, перенести в другую группу, убрать в архив. В архивную группу
 * товар не положить; группу нельзя вложить в её же подгруппу — такие варианты в выборе не показываются.
 */
export function OwnGroupsScreen({ vm, actions }: { vm: OwnGroupsVm; actions: OwnGroupsActions }) {
  const write = vm.writeDisabledReason === null
  const form = vm.form
  return (
    <section className="own-screen" aria-labelledby="own-groups-title">
      <header className="own-toolbar">
        <h2 id="own-groups-title">Группы товаров</h2>
        {form === null && (
          <Button variant="primary" icon={<Plus size={ICON_SIZE} aria-hidden="true" />} disabled={!write} onClick={actions.create}>
            Новая группа
          </Button>
        )}
        <Button variant="ghost" icon={<List size={ICON_SIZE} aria-hidden="true" />} onClick={actions.openProducts}>
          Товары
        </Button>
        <Button variant="ghost" icon={<RefreshCw size={ICON_SIZE} aria-hidden="true" />} busy={vm.query.refreshing} onClick={actions.reload}>
          Обновить
        </Button>
      </header>
      {vm.writeDisabledReason !== null && (
        <p className="own-muted" role="note">
          Создание и правка выключены. {vm.writeDisabledReason}
        </p>
      )}
      {vm.done !== null && (
        <p className="own-problem" role="status">
          <CircleCheck size={ICON_SIZE_SMALL} aria-hidden="true" />
          {vm.done}
        </p>
      )}
      {form !== null && (
        <section className="own-section" aria-labelledby="own-group-form-title">
          <div className="own-section__head">
            <h3 id="own-group-form-title">{form.draft.id === null ? 'Новая группа' : 'Изменить группу'}</h3>
            <Button variant="ghost" icon={<X size={ICON_SIZE} aria-hidden="true" />} onClick={actions.closeForm}>
              Закрыть
            </Button>
          </div>
          <div className="own-row">
            <TextField label="Название" value={form.draft.name} onChange={(name) => actions.draftChanged({ ...form.draft, name })} />
            <SelectField
              label="Внутри группы"
              value={form.draft.parentId}
              options={form.parentOptions}
              onChange={(parentId) => actions.draftChanged({ ...form.draft, parentId })}
            />
          </div>
          {form.draft.id !== null && (
            <CheckboxField
              label="В архиве (новые товары в неё не положить)"
              checked={form.draft.archived}
              onChange={(archived) => actions.draftChanged({ ...form.draft, archived })}
            />
          )}
          <Problem text={form.problem} />
          {form.error !== null && <CommandError title="Группа не записана" error={form.error} />}
          <div className="own-row">
            <Button
              variant="primary"
              icon={<Save size={ICON_SIZE} aria-hidden="true" />}
              busy={form.saving}
              disabled={!write || form.problem !== null}
              onClick={actions.save}
            >
              {form.draft.id === null ? 'Создать' : 'Сохранить'}
            </Button>
          </div>
        </section>
      )}
      <QueryState query={vm.query} emptyText="Групп пока нет — товары лежат без группы" onRetry={actions.reload} what="Группы" />
      {vm.rows.length > 0 && (
        <ul className="own-list" aria-label="Группы">
          {vm.rows.map((row) => (
            <li key={row.id} data-archived={row.archived} className={indent(row.depth)}>
              {row.archived ? <Archive size={ICON_SIZE} aria-hidden="true" /> : <Folder size={ICON_SIZE} aria-hidden="true" />}
              <span title={row.path}>{row.name}</span>
              {row.archived && <span className="own-badge">в архиве</span>}
              {row.depth > 0 && <span className="own-list__meta">{row.path}</span>}
              <Button variant="ghost" icon={<Pencil size={ICON_SIZE} aria-hidden="true" />} disabled={!write} onClick={() => actions.edit(row.id)}>
                Изменить
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
